import type { FC, ReactNode } from "react";

import classNames from "classnames";

import { IconButton } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { isOptedInToDigitalSuite } from "app/common/services/solutions2Helper";
import { EAdsTabs } from "app/components/React/PlaResearch/type";
import { trackEvent } from "app/components/React/Table/SWReactTableTrackEvent";
import { allTrackers } from "app/services/track/track";
import { clearURl } from "pro-features/pages/app performance/src/page/single/usage section/formatters";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { PlainTooltip } from "../../Tooltip/PlainTooltip/PlainTooltip";
import type { ITableCellProps } from "../interfaces/ITableCellProps";

import { UpgradeLink } from "./UpgradeLink";

export interface ISearchKeywordCellProps
    extends Partial<Pick<ITableCellProps, "tableOptions" | "value" | "row" | "onItemClick" | "disabled">> {
    adsUrl?: string;
    withAdsLink?: boolean;
    withGoogleLink?: boolean;
    destinationState?: string;
    customCTA?: ReactNode;
    destinationStateParams?: Record<string, any>;
    navigatorParams?: Record<string, any>;
    openInNewTab?: boolean;
    tooltipContent?: string;
}

export const SearchKeywordCell: FC<ISearchKeywordCellProps> = ({
    tableOptions,
    value,
    tooltipContent,
    row,
    adsUrl,
    onItemClick,
    disabled = false,
    withAdsLink = true,
    withGoogleLink = true,
    destinationState = "organicsearch_keywordAnalysis_overview_2",
    destinationStateParams = {},
    navigatorParams = {},
    openInNewTab,
    customCTA,
}) => {
    const { isWWW, country, duration, webSource, selectedPageTab, key } = proNavigator.getParams();
    const shouldOpenKeywordInNewTab = openInNewTab ?? !isOptedInToDigitalSuite();
    const keywordPageParams = {
        isWWW,
        country,
        duration,
        keyword: encodeURIComponent(value),
        key,
        webSource,
        selectedPageTab,
    };
    const navigationParams = {
        ...navigatorParams,
        keyword: encodeURIComponent(value),
    };
    const externalTracking = (keyword: string) => () =>
        allTrackers.trackEvent("External link", "open", `Table/${keyword}/serp`);
    if (value === "grid.upgrade") {
        return <UpgradeLink hookType="keywords" />;
    }
    let keywordsAnalysisDestUrl = row?.url;
    const searchAdsUrl =
        adsUrl ||
        proNavigator.href("paidsearch_keywordAnalysis_serp_ads", {
            ...keywordPageParams,
            selectedPageTab: EAdsTabs[EAdsTabs.Text],
        });
    if (isWWW && country && duration && webSource) {
        keywordsAnalysisDestUrl = proNavigator.getStateUrl(destinationState, {
            ...keywordPageParams,
            ...destinationStateParams,
        });
    } else if (navigatorParams) {
        keywordsAnalysisDestUrl = proNavigator.getStateUrl(destinationState, {
            ...navigationParams,
            ...destinationStateParams,
        });
    }
    const renderLink = () => {
        if (!keywordsAnalysisDestUrl || proNavigator.current().name?.match(/accountreview_/)) {
            return <span className="swTable-content">{value}</span>;
        }

        return (
            <PlainTooltip
                enabled={!!tooltipContent}
                text={tooltipContent}
                placement="top"
            >
                <a
                    href={disabled ? undefined : clearURl(keywordsAnalysisDestUrl)}
                    className={classNames("swTable-content", disabled && "swTable-content--disabled")}
                    target={shouldOpenKeywordInNewTab ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                    onClick={
                        disabled
                            ? undefined
                            : () => {
                                  if (onItemClick) {
                                      onItemClick(value);
                                  } else {
                                      trackEvent(tableOptions, "Internal Link", value, "click");
                                  }
                              }
                    }
                >
                    {value}
                </a>
            </PlainTooltip>
        );
    };
    return (
        <div className="swTable-keywordCell u-relative u-full-width table-cell-buttons-container separate-text-from-buttons">
            <span className="search-keyword">{renderLink()}</span>
            {!disabled ? (
                <span className="link-buttons-container">
                    {withAdsLink && (adsUrl || webSource === "Desktop") && !chosenSites.isCompare() ? (
                        <PlainTooltip
                            cssClass="plainTooltip-element search-keyword-cell-tooltip"
                            text="analysis.source.search.keywords.table.button.ads"
                            placement="top"
                        >
                            <a
                                href={searchAdsUrl}
                                target={"_blank"}
                                rel="noopener noreferrer"
                            >
                                <IconButton
                                    iconName="search-ads"
                                    type="flat"
                                    iconSize="xs"
                                />
                            </a>
                        </PlainTooltip>
                    ) : null}
                    {withGoogleLink ? (
                        <PlainTooltip
                            cssClass="plainTooltip-element search-keyword-cell-tooltip"
                            text="analysis.source.search.keywords.table.button.serp"
                            placement="top"
                        >
                            <a
                                href={`https://google.com/search?q=${value}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={externalTracking(value)}
                            >
                                <IconButton
                                    iconName="google-search"
                                    type="flat"
                                    iconSize="xs"
                                />
                            </a>
                        </PlainTooltip>
                    ) : null}
                    {customCTA}
                </span>
            ) : null}
        </div>
    );
};
SearchKeywordCell.displayName = "SearchKeywordCell";
